import { Message, MessageBlock } from '@/types';
import { generateId, fetchWithAuth } from './utils';
import { useUIStore } from '@/stores';
// 🔥 Phase 3 完成：移除 registerMessageIds 导入
/**
 * EventSource Client for handling Server-Sent Events (SSE)
 *
 * Message Tracking System:
 * - Backend 事件流程：
 *   - `message_init`: 返回 conversationId, previousMessageId, userMessageId, assistantMessageId
 *   - `message_block`: 流式传输内容块
 *   - `message_end`: 返回 choices, sources 等最终数据
 *
 * - Frontend ID 管理（🔥 重构后）：
 *   - 统一使用 message.id：字符串=临时ID，数字=后端真实ID
 *   - 发送前/流式中：message.id 为字符串（如 "temp-xxx"）
 *   - message_init 后：message.id 更新为数字（后端返回的 assistantMessageId）
 *   - 通过 typeof message.id === 'string' 判断消息是否已收到后端确认
 *   - activeRequests 使用 conversationId:messageId 作为复合键
 *
 * Message ID Flow (重构后):
 * 1. Frontend 创建临时消息: id = pendingId (字符串)
 * 2. message_init: id 更新为 assistantMessageId (数字)
 * 3. message_block: 使用 conversationId + id 匹配消息
 * 4. message_end: isStreaming = false, choices, sources
 */
export class EventSourceClient {
  protected onMessage: (message: Message) => void;
  // 🔥 重构：使用 conversationId:messageId 作为复合键，支持多会话并发
  private activeRequests: Map<string, AbortController> = new Map(); // conversationId:messageId -> AbortController
  private skillTaskPendingIds: Set<string> = new Set(); // 🔥 类级别追踪 skill 任务 pendingId，确保所有错误路径都能检测到
  private currentMessages: Map<
    string,
    {
      id: string | number; // 🔥 重构：字符串=临时ID，数字=后端真实ID
      content: string;
      userContent?: string; // 用户输入内容（在 sendMessage 开始时保存，供 message_start 使用）
      fileIds?: string[]; // 附件文件ID
      conversationId?: string;
      previousMessageId?: number | null;
      backendId?: number;
      completionId?: string; // 后端返回的 completion ID，用于中断请求时调用停止接口
      userMessageId?: number;
      blocks: MessageBlock[];
      tokens?: number;
      inputTokens?: number;
      hasStarted?: boolean; // 标记是否已收到 message_start，用于判断是否应该渲染
      shouldRenderOnInit?: boolean; // 标记是否应该在 message_init 时渲染（队列为空时为 true）
      pendingId?: string; // 🔥 新增：保存原始临时ID，用于 activeRequests key 更新
      isSkillTask?: boolean; // 🔥 新增：标记 skill 任务，SSE 流提前结束时不报错
    }
  > = new Map(); // requestKey -> message data
  // 存储被跳过渲染的任务的用户输入内容，供后续合并使用
  private skippedTaskContents: Map<
    string,
    {
      userContent: string;
      fileIds: string[];
      userMessageId?: number;
      pendingId: string;
    }
  > = new Map();
  constructor(_url: string, onMessage: (message: Message) => void) {
    this.onMessage = onMessage;
  }
  private extractFileName = (filePath: string) => {
    if (!filePath) return '';
    const parts = filePath.split(/[/\\]/);
    return parts[parts.length - 1] || filePath;
  };
  // 辅助方法：创建符合 Message 类型的对象
  // 统一使用 id（string=待确认ID，number=已确认ID）
  private createMessage(params: {
    id: string | number; // 🔥 统一 ID：字符串=临时ID，数字=后端真实ID
    previousMessageId?: number | null;
    backendId?: number;
    completionId?: string; // 后端返回的 completion ID，用于中断请求时调用停止接口
    blocks: MessageBlock[];
    choices?: any[];
    role: 'user' | 'assistant';
    timestamp: Date;
    isStreaming: boolean;
    sources?: any;
    client?: any;
    tokens?: number;
    inputTokens?: number;
    isCanceled?: boolean; // 🔥 新增：是否已被取消
    cancelledMessageId?: number; // 🔥 新增：被取消的消息 ID
  }): Message {
    // 如果后端提供了 choices，使用后端的；否则使用 blocks 创建默认 choice
    const choices = params.choices || [
      {
        version: 0,
        blocks: params.blocks,
      },
    ];
    const msg: Message = {
      id: params.id,
      backendId: params.backendId,
      completionId: params.completionId,
      previousMessageId: params.previousMessageId,
      choices: choices,
      role: params.role,
      timestamp: params.timestamp,
      isStreaming: params.isStreaming,
      sources: params.sources,
      tokens: params.tokens,
      inputTokens: params.inputTokens,
      // 🔥 新增：传递 isCanceled 属性
      isCanceled: params.isCanceled,
    };

    if (params.client) {
      msg.client = params.client;
    }

    return msg;
  }

  private toNumberOrUndefined(value: any): number | undefined {
    if (value === null || value === undefined) return undefined;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  private handleMessageInContext(
    data: any,
    eventType: string,
    pendingId: string
  ) {
    // 为每个请求维护独立的消息状态
    const requestKey = `request_${pendingId}`;
    switch (eventType) {
      case 'message_init': {
        // 后端返回 conversationId 和 previousMessageId（不再返回 sessionId）
        const backendConversationId = data.conversationId;
        const previousMessageId = data.previousMessageId; // 上一条消息的后端 ID
        // 后端返回 clientId 和 clientName
        const clientId = data.clientId;
        const clientName = data.clientName;
        // 后端返回 completionId，用于中断请求时调用停止接口
        const completionId = data.completionId;
        // message_init 时后端返回 userMessageId 和 assistantMessageId
        const userMessageId = data.userMessageId;
        const assistantMessageId = data.assistantMessageId;
        // 🔥 重构：使用后端返回的 assistantMessageId 作为消息ID（数字类型）
        // 如果后端未返回，则保持使用临时ID
        const messageId = assistantMessageId ?? pendingId;

        // 获取已有对象并更新，而不是创建新对象
        // 这样可以保留 sendMessage 时预先保存的 userContent 和 fileIds
        const existingMessage = this.currentMessages.get(requestKey);

        const currentMessage = {
          ...existingMessage, // 保留 userContent 和 fileIds
          id: messageId,
          conversationId: backendConversationId,
          // 🔥 修复：保留前端原本的 previousMessageId，不要被后端返回的值覆盖
          // 前端发送的 previousMessageId 用于构建树结构（如指向 cancel 节点）
          // 后端返回的值存储在 backendPreviousMessageId（如果需要）
          previousMessageId:
            existingMessage?.previousMessageId ?? previousMessageId,
          backendPreviousMessageId: previousMessageId, // 后端返回的原始值
          backendId: assistantMessageId, // message_init 时获得 backendId (assistantMessageId)
          userMessageId, // 保存 userMessageId，用于后续更新 user 消息的 ID
          completionId, // 保存 completionId 用于停止请求
          content: '',
          blocks: [],
          clientId,
          clientName,
          tokens: undefined,
          inputTokens: undefined,
          // 标记是否已收到 message_start
          // 如果 message_end 时 hasStarted 为 false，说明没有实质内容，跳过渲染
          hasStarted: false,
          // 🔥 重构：保存原始临时ID，用于 activeRequests key 更新
          pendingId,
        };
        // 使用 pendingId 作为键，确保每个请求的消息状态独立
        this.currentMessages.set(requestKey, currentMessage);

        // 🔥 Phase 3 完成：移除 registerMessageIds 调用
        // ID 映射不再需要，消息通过 message.id (pendingId) 直接匹配

        // message_init 不再调用 createMessage，只保存基础信息
        // createMessage 逻辑移到 message_start 事件中

        // 设置当前正在流式输出的 assistantMessageId，用于等待任务队列
        // 新发起的 message 的 previousMessageId 应该是这个值
        if (assistantMessageId) {
          useUIStore
            .getState()
            .setCurrentStreamingAssistantMessageId(assistantMessageId);
          useUIStore.getState().setStreamingActive(true);
        }

        // 更新任务状态为 'initialized'（已收到 message_init）
        // 并设置该任务的 assistantMessageId 和 userMessageId，供后续任务使用
        useUIStore
          .getState()
          .updateTaskStatus(
            pendingId,
            'initialized',
            assistantMessageId,
            userMessageId
          );

        // 🔥 新增：存储当前 conversation 最新的 message_init 信息（用于 Cancel 操作）
        // 🔥 使用后端返回的 backendConversationId 作为主 key
        console.log('📍 setLatestMessageInit 存储', {
          backendConversationId,
          originalConversationId: existingMessage?.conversationId,
          userMessageId,
          assistantMessageId,
          pendingId,
        });
        useUIStore.getState().setLatestMessageInit({
          conversationId: backendConversationId,
          userMessageId,
          assistantMessageId,
          pendingId,
        });

        // 🔥 如果原始 conversationId 不同（如新对话使用 'new' 或临时 ID），也存储一份
        // 这样 TreeMessageRenderer 无论使用哪个 ID 都能找到数据
        const originalConversationId = existingMessage?.conversationId;
        if (
          originalConversationId &&
          originalConversationId !== backendConversationId
        ) {
          useUIStore.getState().setLatestMessageInit({
            conversationId: originalConversationId,
            userMessageId,
            assistantMessageId,
            pendingId,
          });
          console.log('📍 同时存储原始 conversationId 的 latestInit', {
            originalConversationId,
            backendConversationId,
          });

          // 🔥 修复：迁移 activeStreamingMessages 从临时 ID 到真实 ID
          // 这样 Stop 按钮在新对话中才能正确显示
          // 因为 UI 会用真实的 backendConversationId 来检查 activeStreamingMessages
          useUIStore
            .getState()
            .removeActiveStreamingMessage(originalConversationId, pendingId);
          useUIStore
            .getState()
            .addActiveStreamingMessage(backendConversationId, pendingId);
          console.log('📍 迁移 activeStreamingMessages', {
            from: originalConversationId,
            to: backendConversationId,
            pendingId,
          });
        }

        // 收到 message_init 后，清除等待状态，允许发送下一个消息
        useUIStore.getState().setIsWaitingForMessageInit(false);

        // 🔥 检查统一任务队列：如果有待取消的任务（带有 targetPendingId），更新其目标 ID
        // 取消任务会在队列处理时执行，而不是立即执行
        const { taskQueue, updateCancelTaskTargetIds } = useUIStore.getState();
        const hasPendingCancelTask = taskQueue.some(
          t => t.type === 'cancel' && t.targetPendingId === pendingId
        );
        if (hasPendingCancelTask && userMessageId) {
          console.log('📝 更新 cancel 任务的目标 ID（等待队列处理）', {
            pendingId,
            userMessageId,
            previousMessageId,
          });

          // 更新 cancel 任务的 targetUserMessageId（取消操作会在队列处理时执行）
          updateCancelTaskTargetIds(
            pendingId,
            userMessageId,
            previousMessageId
          );
        }

        // 🔥 调试日志：检查 userContent 是否存在
        console.log('📝 准备渲染消息 - userContent 检查:', {
          hasUserContent: !!currentMessage.userContent,
          userContentLength: currentMessage.userContent?.length || 0,
          conversationId: currentMessage.conversationId,
        });

        // 🔥 修改：消息已在 sendMessage 时立即渲染，这里只需更新后端 ID
        // 发送一个更新消息来更新已渲染消息的后端 ID
        if (currentMessage.userContent) {
          // 创建更新消息，用于更新已渲染消息的后端 ID
          const updateMessage = this.createMessage({
            id: currentMessage.id, // 使用后端返回的 assistantMessageId
            previousMessageId: currentMessage.previousMessageId,
            backendId: currentMessage.backendId,
            completionId: currentMessage.completionId,
            blocks: [],
            choices: [{ version: 0, blocks: [] }],
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: true,
            client: clientId
              ? { id: clientId, name: clientName || clientId }
              : undefined,
            tokens: undefined,
            inputTokens: undefined,
          });
          (updateMessage as any).conversationId = currentMessage.conversationId;
          (updateMessage as any).pendingId = pendingId; // 用于匹配已渲染的消息
          (updateMessage as any).messageStatus = 'pending'; // 🔥 修改：已发送请求，等待 message_start
          (updateMessage as any)._isBackendIdUpdate = true; // 标记这是后端 ID 更新

          // 附加 user 信息更新（包含后端返回的 userMessageId）
          (updateMessage as any)._userInfo = {
            blocks: [], // 不需要重新渲染 blocks
            backendId: currentMessage.userMessageId,
            fileIds: currentMessage.fileIds,
          };

          console.log('📝 message_init: 更新已渲染消息的后端 ID', {
            pendingId,
            newId: currentMessage.id,
            userMessageId: currentMessage.userMessageId,
          });

          // 🔥 Skill 模式：跳过 onMessage，WS handler 已通过 message_init 事件处理
          // 避免 SSE 的 _isBackendIdUpdate 与 WS handler 同时修改 conv.messages 导致 user 消息重复
          if (!this.skillTaskPendingIds.has(pendingId)) {
            this.onMessage(updateMessage);
          } else {
            console.log(
              '🎯 [Skill] message_init: skipping onMessage, WS handler owns rendering'
            );
          }

          // 标记已在 init 时渲染
          currentMessage.hasStarted = true;
          this.currentMessages.set(requestKey, currentMessage);
        }

        // 🔥 检查是否有等待中的任务需要发送
        // 触发事件通知 ChatPage 发送下一个任务
        const nextTask = useUIStore.getState().getNextPendingTask();
        if (nextTask) {
          // 🔥 修复：只有当下一个任务属于同一个会话时，才传递 previousMessageId
          // 如果是新会话（temp ID）或不同会话，previousMessageId 应该是 null
          // 这避免了新会话错误地使用旧会话的 assistantMessageId 导致消息串台
          const isSameConversation =
            nextTask.conversationId === currentMessage.conversationId ||
            nextTask.conversationId === backendConversationId;
          const isNewConversation =
            nextTask.conversationId?.startsWith('temp-') || false;

          // 新会话的第一条消息不需要 previousMessageId
          // 同一会话的后续消息使用上一条消息的 assistantMessageId
          const nextPreviousMessageId =
            isNewConversation || !isSameConversation
              ? null
              : assistantMessageId;
          // 使用自定义事件通知 ChatPage 发送下一个任务
          window.dispatchEvent(
            new CustomEvent('sendNextPendingTask', {
              detail: {
                task: nextTask,
                previousMessageId: nextPreviousMessageId,
              },
            })
          );
        }
        break;
      }
      case 'message_start': {
        // message_start 事件：在 message_init 之后触发
        // 修改：去掉 mergedMessageIds 合并逻辑，每条消息单独渲染
        const currentMsg = this.currentMessages.get(requestKey);
        if (!currentMsg) {
          console.error(
            '❌ No current message found for message_start:',
            pendingId
          );
          break;
        }

        // 标记已收到 message_start，表示消息有实质内容
        currentMsg.hasStarted = true;
        this.currentMessages.set(requestKey, currentMsg);

        // message_start 收到后，表示当前消息已经开始正式处理
        // 更新任务状态为 'streaming'
        useUIStore.getState().updateTaskStatus(pendingId, 'streaming');
        console.log('🎬 任务开始流式输出', {
          taskId: pendingId,
        });

        // 更新消息状态为 'ongoing'（正在处理）
        // 由于消息已在 message_init 时创建，这里只需要发送状态更新
        const ongoingMessage = this.createMessage({
          id: currentMsg.id,
          previousMessageId:
            currentMsg.userMessageId || currentMsg.previousMessageId,
          backendId: currentMsg.backendId,
          completionId: currentMsg.completionId,
          blocks: [],
          choices: [{ version: 0, blocks: [] }],
          role: 'assistant',
          timestamp: new Date(),
          isStreaming: true,
          client: (currentMsg as any).clientId
            ? {
                id: (currentMsg as any).clientId,
                name:
                  (currentMsg as any).clientName ||
                  (currentMsg as any).clientId,
              }
            : undefined,
          tokens: undefined,
          inputTokens: undefined,
        });
        (ongoingMessage as any).conversationId = currentMsg.conversationId;
        (ongoingMessage as any).pendingId = pendingId; // 🔥 添加 pendingId 用于节点查找
        if (currentMsg.userMessageId) {
          (ongoingMessage as any).userMessageId = currentMsg.userMessageId;
        }
        // 设置消息状态为 'ongoing'
        (ongoingMessage as any).messageStatus = 'ongoing';

        // 🔥 Skill 模式：跳过 onMessage，WS handler 已通过 content/text 事件处理
        // 避免 SSE 的 ongoing 状态更新与 WS handler 同时修改 conv.messages 导致重复
        if (!this.skillTaskPendingIds.has(pendingId)) {
          this.onMessage(ongoingMessage);

          // 同时更新 user 消息的状态为 'ongoing'
          // 通过发送一个只包含状态更新的 user 消息来触发更新
          if (currentMsg.userMessageId || currentMsg.userContent) {
            const userStatusUpdate = this.createMessage({
              id: `user-status-${pendingId}`, // 使用一个不会匹配的 ID
              previousMessageId: currentMsg.previousMessageId,
              backendId: currentMsg.userMessageId,
              blocks: [], // 空 blocks，不会覆盖原有内容
              role: 'user',
              timestamp: new Date(),
              isStreaming: false,
            });
            (userStatusUpdate as any).conversationId =
              currentMsg.conversationId;
            (userStatusUpdate as any).messageStatus = 'ongoing';
            (userStatusUpdate as any)._isStatusOnlyUpdate = true; // 标记这是状态更新
            this.onMessage(userStatusUpdate);
            console.log('🔄 更新 user 消息状态为 ongoing', { pendingId });
          }
        } else {
          console.log(
            '🎯 [Skill] message_start: skipping onMessage, WS handler owns rendering'
          );
        }

        break;
      }
      case 'message_block': {
        let currentMsg = this.currentMessages.get(requestKey);
        if (!currentMsg) {
          console.error('❌ No current message found for request:', pendingId);
          break;
        }
        // 处理不同类型的数据
        if (data.type === 'ref') {
          const url = `${window.location.origin}${encodeURIComponent(`/api/files/retrieve?filePath=${data.file}&folderID=${data.folderID}`)}${data.page ? `#page=${data.page}` : ''}`;
          const htmlLink = `[${data.order}] <a href="#" data-pdf-url="${url}" data-pdf-filename="${data.file}" style="color: #0066cc; text-decoration: underline; cursor: pointer;">${this.extractFileName(data.file)}</a>`;
          currentMsg.blocks = [
            ...currentMsg.blocks,
            {
              type: 'ref',
              content: htmlLink,
            },
          ];
          currentMsg.content += htmlLink;
        } else if (
          data.type === 'think' ||
          data.type === 'call' ||
          data.type === 'result'
        ) {
          const thinkContent = data.content || '';
          const lastBlock = currentMsg.blocks[currentMsg.blocks.length - 1];
          if (lastBlock && lastBlock?.type === data.type) {
            // 创建新的 blocks 数组和新的 block 对象，确保 React 能检测到变化
            const updatedBlock = {
              ...lastBlock,
              content: lastBlock.content + thinkContent,
            };
            currentMsg.blocks = [
              ...currentMsg.blocks.slice(0, -1),
              updatedBlock,
            ];
          } else {
            currentMsg.blocks = [
              ...currentMsg.blocks,
              {
                id: generateId(),
                type: data.type,
                content: thinkContent,
                collapsed: false,
              },
            ];
          }
          currentMsg.content += thinkContent;
        } else if (data.type === 'hash-code') {
          const hashCodeContent = data.content || '';
          currentMsg.blocks = [
            ...currentMsg.blocks,
            {
              type: 'hash-code',
              content: hashCodeContent,
            },
          ];
          currentMsg.content += hashCodeContent;
        } else if (data.type === 'text') {
          const textContent = data.content || '';
          const lastBlock = currentMsg.blocks[currentMsg.blocks.length - 1];
          if (lastBlock && lastBlock?.type === 'text' && data.type === 'text') {
            // 创建新的 blocks 数组和新的 block 对象，确保 React 能检测到变化
            const updatedBlock = {
              ...lastBlock,
              content: lastBlock.content + textContent,
            };
            currentMsg.blocks = [
              ...currentMsg.blocks.slice(0, -1),
              updatedBlock,
            ];
          } else {
            currentMsg.blocks = [
              ...currentMsg.blocks,
              {
                type: 'text',
                content: textContent,
              },
            ];
          }
          currentMsg.content += textContent;
          // 创建新的 blocks 数组，将 think 类型的 block 折叠
          currentMsg.blocks = currentMsg.blocks.map(block => {
            if (block.type === 'think' && !block.collapsed) {
              return { ...block, collapsed: true };
            }
            return block;
          });
        } else if (data.type === 'file') {
          // Handle file type (image rendering)
          const fileId = data.content || '';
          currentMsg.blocks = [
            ...currentMsg.blocks,
            {
              type: 'file',
              content: fileId,
            },
          ];
          currentMsg.content += `[Image: ${fileId}]`;
        } else {
          const content = data.content || '';
          currentMsg.content += content;
          if (content) {
            currentMsg.blocks = [
              ...currentMsg.blocks,
              {
                type: 'text',
                content: content,
              },
            ];
          }
        }
        this.currentMessages.set(requestKey, currentMsg);
        const blockMessage = this.createMessage({
          id: currentMsg.id,
          // 🔥 修复：使用 userMessageId 作为 previousMessageId，这样才能正确匹配 user 节点
          // currentMsg.previousMessageId 是上一条 assistant 消息的 ID，不是当前 user 消息的 ID
          previousMessageId:
            currentMsg.userMessageId || currentMsg.previousMessageId,
          backendId: currentMsg.backendId,
          completionId: currentMsg.completionId, // 传递 completionId 用于停止请求
          blocks: [...currentMsg.blocks],
          // 修复：添加 choices 字段，因为 updateConversationMessage 使用 message.choices?.[0]?.blocks 来更新
          choices: [{ version: 0, blocks: [...currentMsg.blocks] }],
          role: 'assistant',
          timestamp: new Date(),
          isStreaming: true,
          tokens: currentMsg.tokens,
          inputTokens: currentMsg.inputTokens,
        });
        // 修复：添加 conversationId 到 blockMessage，确保 handleIncomingMessage 能正确路由
        (blockMessage as any).conversationId = currentMsg.conversationId;
        // 🔥 Skill 模式：跳过 onMessage，WS handler 已通过 content/text 事件处理相同内容
        // 避免 SSE 和 WS 两个通道同时更新 blocks 导致第一个 text block 渲染两次
        if (!this.skillTaskPendingIds.has(pendingId)) {
          this.onMessage(blockMessage);
        }
        break;
      }
      case 'message_end': {
        const endMessage = this.currentMessages.get(requestKey);
        if (endMessage) {
          if (data.content) {
            if (data.type === 'hash-code') {
              endMessage.blocks.push({
                type: 'hash-code',
                content: data.content,
              });
            } else {
              endMessage.blocks.push({
                type: 'text',
                content: data.content,
              });
            }
          }
          // userMessageId 和 assistantMessageId 已在 message_init 中获取
          // message_end 时不再需要处理这些字段

          // message_end 返回的 token 统计（inputTokens + tokens）
          endMessage.inputTokens = this.toNumberOrUndefined(data.inputTokens);
          endMessage.tokens = this.toNumberOrUndefined(data.tokens);
          // 处理 choices：如果后端返回了 choices 数组且非空，则使用它
          // 修复：检查 data.choices.length > 0，避免空数组覆盖已累积的 blocks
          let finalChoices: any[] | undefined = undefined;
          if (
            data.choices &&
            Array.isArray(data.choices) &&
            data.choices.length > 0
          ) {
            // 转换后端的 choices 格式
            finalChoices = data.choices.map((choice: any) => {
              const blocks =
                choice.contents?.map((item: any) => ({
                  type: item.type || 'text',
                  content: String(item.content || ''),
                })) || [];
              return {
                version: choice.version || 0,
                blocks: blocks,
              };
            });
            // 额外检查：如果转换后的 choices 全是空 blocks，也不使用
            const hasActualContent = finalChoices?.some(
              choice =>
                choice.blocks &&
                choice.blocks.length > 0 &&
                choice.blocks.some((b: any) => b.content && b.content.trim())
            );
            if (!hasActualContent) {
              console.log('⚠️ choices 存在但内容为空，使用累积的 blocks');
              finalChoices = undefined;
            }
          } else {
            console.log(
              '⚠️ No choices found in message_end data, using accumulated blocks'
            );
          }
          const finalMessage = this.createMessage({
            id: endMessage.id, // 使用 message.id（可能是临时ID或真实ID）
            previousMessageId: endMessage.previousMessageId,
            backendId: endMessage.backendId, // 使用 message_init 中获取的 backendId (assistantMessageId)
            blocks: endMessage.blocks,
            choices: finalChoices, // 使用后端返回的 choices（如果有）
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: false,
            sources: data.sources,
            client: data.client,
            tokens: endMessage.tokens,
            inputTokens: endMessage.inputTokens,
          });
          // 将 userMessageId 添加到 finalMessage（使用 message_init 中获取的值）
          if (endMessage.userMessageId) {
            (finalMessage as any).userMessageId = endMessage.userMessageId;
          }
          // 🔥 修复：添加 conversationId 到 finalMessage，确保 handleIncomingMessage 能正确路由
          (finalMessage as any).conversationId = endMessage.conversationId;
          // 🔥 修复：添加 pendingId 到 finalMessage，用于备用会话查找
          (finalMessage as any).pendingId = endMessage.pendingId;
          // 🔥 新增：消息结束时移除状态标签（设置为 undefined）
          (finalMessage as any).messageStatus = undefined;

          // 🔥 修复：只有当消息完全没有内容时才跳过渲染
          // 之前的逻辑会跳过只包含 hash code 的消息，但用户期望看到 hash code
          // 同时，即使 hasStarted 为 false，如果有内容也应该渲染
          const hasNoContent =
            endMessage.blocks.length === 0 && !endMessage.content;
          // 只有完全没有内容时才跳过渲染
          const shouldSkipRender = hasNoContent;

          // 关键修复：在调用 onMessage 之前先移除任务
          // 这样 handleIncomingMessage 中检查 hasOtherActiveTasks 时，当前任务已不在队列中
          useUIStore.getState().removeTask(pendingId);

          // 🔥 新增：从活跃流式消息 Map 中移除（用于控制  按钮显示）
          if (endMessage.conversationId) {
            useUIStore
              .getState()
              .removeActiveStreamingMessage(
                endMessage.conversationId,
                pendingId
              );
          }

          if (shouldSkipRender) {
            // 保存被跳过任务的用户输入内容，供后续合并使用
            // 虽然不渲染，但保留用户输入信息，以便后续合并任务能获取这些内容
            if (endMessage.userContent) {
              this.skippedTaskContents.set(pendingId, {
                userContent: endMessage.userContent,
                fileIds: endMessage.fileIds || [],
                userMessageId: endMessage.userMessageId,
                pendingId,
              });
            }
            // 🔥 修复：即使跳过渲染，也需要发送状态更新来清除 loading 状态
            // 否则切换会话后，原会话的 loading 状态永远无法清除
            const loadingClearMessage = this.createMessage({
              id: endMessage.id,
              previousMessageId: endMessage.previousMessageId,
              backendId: endMessage.backendId,
              blocks: [],
              role: 'assistant',
              timestamp: new Date(),
              isStreaming: false,
            });
            (loadingClearMessage as any).conversationId =
              endMessage.conversationId;
            (loadingClearMessage as any).pendingId = endMessage.pendingId;
            (loadingClearMessage as any)._isLoadingClearOnly = true;
            this.onMessage(loadingClearMessage);
          } else {
            this.onMessage(finalMessage);
          }

          // 同时清除 user 消息的状态标签
          // message_end 只处理了 assistant 消息，user 消息的状态也需要清除
          if (endMessage.userMessageId || endMessage.userContent) {
            const userStatusClear = this.createMessage({
              id: `user-clear-${pendingId}`,
              previousMessageId: endMessage.previousMessageId,
              backendId: endMessage.userMessageId,
              blocks: [],
              role: 'user',
              timestamp: new Date(),
              isStreaming: false,
            });
            (userStatusClear as any).conversationId = endMessage.conversationId;
            (userStatusClear as any).messageStatus = undefined;
            (userStatusClear as any)._isStatusOnlyUpdate = true;
            this.onMessage(userStatusClear);
            console.log('🔄 清除 user 消息状态标签', { pendingId });
          }

          // 清理已完成的消息
          this.currentMessages.delete(requestKey);

          // 消息结束时，清除流式状态
          useUIStore.getState().setStreamingActive(false);
          useUIStore.getState().setCurrentStreamingAssistantMessageId(null);
          console.log('✅ 任务已完成并移除', { taskId: pendingId });

          // 🔥 新增：检查是否所有任务都已完成，如果是则触发数据刷新
          const { taskQueue } = useUIStore.getState();
          const remainingActiveTasks = taskQueue.filter(
            t =>
              t.status === 'pending' ||
              t.status === 'sent' ||
              t.status === 'initialized' ||
              t.status === 'streaming'
          );
          if (remainingActiveTasks.length === 0) {
            console.log('🔄 所有任务已完成，触发数据刷新事件', {
              conversationId: endMessage.conversationId,
            });
            // 发送自定义事件通知 Chat 组件刷新后端数据
            window.dispatchEvent(
              new CustomEvent('allTasksCompleted', {
                detail: {
                  conversationId: endMessage.conversationId,
                },
              })
            );
          }
        } else {
          console.error('❌ No endMessage found for request:', pendingId);
        }
        break;
      }
      case 'message_error': {
        console.error(
          '❌ EventSource: message_error received in context',
          pendingId,
          data.content || data.error
        );
        // Try to get the current message that was streaming
        const errorMessage = this.currentMessages.get(requestKey);
        // 🔥 修改：由于消息已在 sendMessage 时立即渲染，这里只需更新已渲染消息的内容为错误信息
        // 构建错误消息内容，添加加粗的 "Error: " 前缀
        const rawErrorContent = data.content || data.error || 'Unknown error';
        const errorContent = `**Error:** ${rawErrorContent}`;

        let errorBlocks: MessageBlock[];

        // 🔥 修复：如果已有部分内容，保留并追加错误信息；否则只显示错误
        // 之前的代码会导致错误消息被添加两次
        if (
          errorMessage &&
          errorMessage.blocks &&
          errorMessage.blocks.length > 0
        ) {
          // 有已有内容，追加错误信息
          errorBlocks = [
            ...errorMessage.blocks,
            {
              type: 'text',
              content: `\n\n${errorContent}`,
            },
          ];
        } else {
          // 没有已有内容，只显示错误信息
          errorBlocks = [
            {
              type: 'text',
              content: errorContent,
            },
          ];
        }

        // 更新 currentMessages
        if (errorMessage) {
          errorMessage.blocks = errorBlocks;
          (errorMessage as any).hasError = true;
          this.currentMessages.set(requestKey, errorMessage);
        }

        // 创建更新消息，用于更新已渲染的 assistant 消息
        const finalErrorMessage = this.createMessage({
          id: errorMessage?.id || pendingId, // 使用 pendingId 匹配已渲染的消息
          previousMessageId: errorMessage?.previousMessageId,
          backendId: errorMessage?.backendId,
          blocks: errorBlocks,
          choices: [{ version: 0, blocks: errorBlocks }], // 🔥 添加 choices，finalizeMessage 需要这个字段
          role: 'assistant',
          timestamp: new Date(),
          isStreaming: false, // 🔥 关键：设置为 false 结束 loading 状态
          tokens: 0, // 显示 tokens: 0
        });

        // 添加路由信息
        (finalErrorMessage as any).conversationId =
          errorMessage?.conversationId || data.conversationId;
        (finalErrorMessage as any).pendingId = pendingId; // 用于匹配已渲染的消息
        (finalErrorMessage as any).hasError = true;
        (finalErrorMessage as any).messageStatus = undefined; // 🔥 清除 waiting 状态

        console.log('❌ message_error: 更新已渲染消息为错误信息', {
          pendingId,
          errorContent,
        });

        // 🔥 关键修复：在调用 onMessage 之前先清理状态（与 message_end 保持一致的顺序）
        // 确保 handleIncomingMessage 中 hasActiveStreaming 和 hasPendingTasks 检查能正确返回 false
        // 否则 isLoading 永远不会被设置为 false，导致会话列表持续显示 "Thinking..."

        // 1. 从活跃流式消息 Map 中移除（用于控制 Stop 按钮显示）
        const errorConversationId =
          errorMessage?.conversationId || data.conversationId;
        if (errorConversationId) {
          useUIStore
            .getState()
            .removeActiveStreamingMessage(errorConversationId, pendingId);
        }

        // 2. 清除流式状态
        useUIStore.getState().setStreamingActive(false);
        useUIStore.getState().setCurrentStreamingAssistantMessageId(null);

        // 3. 更新任务状态为 'error'，保留错误信息供 UI 显示
        const rawTaskErrorMessage =
          data.content || data.error || 'Unknown error';
        const taskErrorMessage = `**Error:** ${rawTaskErrorMessage}`;
        useUIStore
          .getState()
          .updateTaskStatus(
            pendingId,
            'error',
            undefined,
            undefined,
            taskErrorMessage
          );

        // 4. 清除等待 message_init 状态，防止发送按钮永久禁用
        useUIStore.getState().setIsWaitingForMessageInit(false);

        // 5. 最后调用 onMessage，此时活跃流式消息和任务状态已清理完毕
        //    handleIncomingMessage 中的 hasActiveStreaming 和 hasPendingTasks 才能正确返回 false
        this.onMessage(finalErrorMessage);

        // 🔥 修改：区分 HTTP 请求级别错误和 SSE message_error 事件
        const hasValidBackendId = errorMessage?.backendId !== undefined;

        if (hasValidBackendId) {
          // SSE message_error 事件：有有效的 assistantMessageId（已收到 message_init）
          // 继续发送等待队列中的下一个任务
          console.log(
            '📝 SSE message_error: 有有效的 backendId，继续发送等待队列',
            {
              taskId: pendingId,
              backendId: errorMessage?.backendId,
            }
          );
          // sendNextPendingTask 事件已在 message_init 时触发，队列会继续处理
        } else {
          // HTTP 请求级别错误：没有收到 message_init，没有有效的 assistantMessageId
          // 清空队列并删除已渲染的 waiting 节点
          console.log('❌ HTTP 请求错误: 清空等待队列和 waiting 节点', {
            taskId: pendingId,
          });
          useUIStore.getState().clearWaitingTasks();

          // 通知 UI 删除所有 waiting 状态的节点
          window.dispatchEvent(
            new CustomEvent('clearWaitingNodes', {
              detail: {
                conversationId:
                  errorMessage?.conversationId || data.conversationId,
              },
            })
          );
        }
        break;
      }
      default:
        console.warn('Unknown message type in context:', eventType);
    }
  }
  async sendMessage(
    content: string,
    conversationId: string,
    pendingId: string, // 待确认消息ID，用于跟踪请求
    projectId: string,
    agentId?: string | null,
    options?: any,
    lastMessageId?: number // 用于指定父消息的ID（previousMessageId）
  ) {
    const requestKey = `request_${pendingId}`;

    // Create new AbortController for this specific request
    const abortController = new AbortController();
    this.activeRequests.set(pendingId, abortController);

    // 🔥 新增：添加到活跃流式消息 Map（用于控制 Stop 按钮显示）
    useUIStore.getState().addActiveStreamingMessage(conversationId, pendingId);

    try {
      // 预先保存用户输入内容，供 message_start 使用
      // 这样在收到 message_init 时可以更新这个对象，而不是创建新的

      // 检查任务队列是否为空，决定是否在 message_init 时渲染
      // 如果队列为空（没有其他等待中的任务），则在 message_init 时渲染
      // 否则在 message_start 时渲染（需要等待可能的合并）
      const { taskQueue } = useUIStore.getState();
      const hasOtherWaitingTasks = taskQueue.some(
        t =>
          t.id !== pendingId &&
          (t.status === 'pending' ||
            t.status === 'sent' ||
            t.status === 'initialized' ||
            t.status === 'streaming')
      );
      const shouldRenderOnInit = !hasOtherWaitingTasks;

      console.log('📤 sendMessage - 队列状态', {
        pendingId,
        hasOtherWaitingTasks,
        shouldRenderOnInit,
        taskQueueLength: taskQueue.length,
      });

      // 发送消息前设置等待 message_init 状态
      useUIStore.getState().setIsWaitingForMessageInit(true);

      // 🔥 修复：计算并保存 previousMessageId，确保 message_init 时能正确使用
      const frontendPreviousMessageId =
        options?.frontendPreviousMessageId ?? lastMessageId;

      this.currentMessages.set(requestKey, {
        id: pendingId,
        content: '',
        userContent: content, // 保存用户输入内容
        fileIds: options?.fileIds, // 保存附件文件ID
        blocks: [],
        tokens: undefined,
        inputTokens: undefined,
        shouldRenderOnInit: true, // 始终在 message_init 时渲染，不再依赖队列状态
        pendingId, // 🔥 新增：保存原始临时ID
        conversationId, // 保存会话ID用于立即渲染
        previousMessageId: frontendPreviousMessageId, // 🔥 修复：保存前端的 previousMessageId
        isSkillTask:
          !!options?.isSkillTask ||
          useUIStore.getState().isSkillConversation(conversationId), // 🔥 标记 skill 任务（包括已标记的 skill 会话）
      });

      // 🔥 判断是否为 skill 模式：当前消息有 isSkillTask 标记 OR 该会话已被标记为 skill 会话
      // 一旦会话进入 skill 模式，后续所有消息（不管有没有 [skill:TFL] 前缀）都自动走 skill 模式
      const isSkillMode =
        !!options?.isSkillTask ||
        useUIStore.getState().isSkillConversation(conversationId);

      // 🔥 类级别追踪 skill 任务（比 currentMessages 更可靠，不会被事件处理器覆盖）
      if (isSkillMode) {
        this.skillTaskPendingIds.add(pendingId);
        console.log('🎯 [Skill] Registered pendingId:', pendingId, {
          isSkillTask: !!options?.isSkillTask,
          isSkillConversation: useUIStore
            .getState()
            .isSkillConversation(conversationId),
        });

        // 🔥 注册 skill 会话（首次进入时注册，已注册的会话不重复注册）
        if (!useUIStore.getState().isSkillConversation(conversationId)) {
          useUIStore.getState().addSkillConversation(conversationId);
          console.log('🎯 [Skill] addSkillConversation:', conversationId);
        }

        // 🔥 主动加入 WebSocket 房间，确保能接收后端推送的流式数据
        try {
          const { default: socketSvc } = await import('@/lib/socket');
          if (socketSvc.isSocketConnected()) {
            socketSvc.emit('join_conversation_room', conversationId);
            console.log('🔔 [Skill] Joined conversation room:', conversationId);
          }
        } catch (e) {
          console.warn('⚠️ [Skill] Failed to join conversation room:', e);
        }
      }

      // 🔥 新增：发送请求前立即渲染 user 消息和 loading 状态的 assistant 消息
      // 不需要等待 message_init
      // 🔥 Skill 模式跳过立即渲染：WebSocket handler 会完全接管消息创建（避免重复消息）
      if (!isSkillMode) {
        // 构建用户消息的 blocks
        const userBlocks: MessageBlock[] = [];
        if (options?.fileIds && Array.isArray(options.fileIds)) {
          for (const fileId of options.fileIds) {
            userBlocks.push({
              type: 'file',
              content: fileId,
            });
          }
        }
        userBlocks.push({
          type: 'text',
          content: content,
        });

        // 创建 assistant 消息（loading 状态）
        // 🔥 修改：使用 frontendPreviousMessageId 来正确链接到未完成的任务
        // 当有未完成任务时，新消息应该链接到那个任务的 pendingId，而不是共同的父节点
        const assistantMessage = this.createMessage({
          id: pendingId, // 使用临时ID，后续 message_init 会更新为后端ID
          previousMessageId: frontendPreviousMessageId, // 🔥 使用前面计算的值，确保一致性
          blocks: [],
          choices: [{ version: 0, blocks: [] }],
          role: 'assistant',
          timestamp: new Date(),
          isStreaming: true,
          client: (() => {
            if (!agentId) return undefined;
            const agent = useUIStore
              .getState()
              .agents.find(a => String(a.id) === String(agentId));
            return {
              id: agentId,
              name: agent?.name || agentId,
              logo: agent?.logo,
            };
          })(),
          tokens: undefined,
          inputTokens: undefined,
        });
        // 添加会话ID用于路由
        (assistantMessage as any).conversationId = conversationId;
        (assistantMessage as any).pendingId = pendingId;
        // 🔥 修复：根据队列状态设置初始消息状态
        // 如果有其他等待中的任务，这条消息应该是 'waiting'（排队中，可取消）
        // 如果没有其他任务，这条消息直接处理，状态是 'pending'（已发送请求）
        (assistantMessage as any).messageStatus = hasOtherWaitingTasks
          ? 'waiting'
          : 'pending';

        // 🔥 关键：附加 user 信息，让 handleIncomingMessage 在同一个 setState 中创建两个节点
        (assistantMessage as any)._userInfo = {
          blocks: userBlocks,
          backendId: undefined, // 后端ID在 message_init 时更新
          fileIds: options?.fileIds,
        };

        console.log('📤 立即渲染消息（不等待 message_init）', {
          pendingId,
          conversationId,
          hasUserContent: !!content,
        });

        this.onMessage(assistantMessage);
      } // end of !isSkillTask block

      // 发送消息前设置等待 message_init 状态
      useUIStore.getState().setIsWaitingForMessageInit(true);

      // 🔥 新增：确保 previousMessageId 是数字或 null，不能是 pending ID 字符串
      // lastMessageId 类型是 number | undefined，但运行时可能从其他地方收到字符串
      let validPreviousMessageId: number | null = null;
      if (lastMessageId !== undefined) {
        // 使用类型断言以允许运行时类型检查（防止意外的字符串值）
        const lastMsgId = lastMessageId as unknown;
        if (typeof lastMsgId === 'number') {
          validPreviousMessageId = lastMsgId;
        } else if (typeof lastMsgId === 'string') {
          // 检查是否是 pending ID 或 user-xxx 格式
          if (
            !lastMsgId.startsWith('pending-') &&
            !lastMsgId.startsWith('user-')
          ) {
            const numId = Number(lastMsgId);
            if (!isNaN(numId)) {
              validPreviousMessageId = numId;
            }
          } else {
            console.warn(
              '⚠️ previousMessageId 是 pending ID，将使用 null:',
              lastMsgId
            );
          }
        }
      }

      const formData: any = {
        message: content,
        previousMessageId: validPreviousMessageId,
      };
      // 只在conversationId存在、不为空且不是临时ID时才添加到formData
      // 临时ID以"temp-"开头，仅用于前端区分不同的新对话，不发送给后端
      // 后端会为新对话创建真正的conversationId并在message_init事件中返回
      if (
        conversationId &&
        conversationId.trim() !== '' &&
        !conversationId.startsWith('temp-')
      ) {
        formData.conversationId = conversationId;
      } else {
        console.log('⚠️ NOT adding conversationId to formData');
      }
      if (projectId) {
        formData.projectId = projectId;
      }
      if (agentId) {
        formData.clientId = agentId;
      }
      // Add fileIds if present
      if (options?.fileIds && options.fileIds.length > 0) {
        formData.fileIds = options.fileIds;
      }
      // Add search options
      if (options) {
        formData.config = options || {};
      }
      // 🔥 Skill 任务：将 sid 提取到 formData 顶层，后端需要它来路由 WebSocket 响应
      if (options?.sid) {
        formData.sid = options.sid;
      }
      // if (options?.allProjects !== undefined && !projectId) {
      //   formData.allProjects = options.allProjects;
      // }
      // if (options?.thinkType !== undefined) {
      //   formData.thinkType = options.thinkType;
      // }
      // if (attachments && attachments.length > 0) {
      //   formData['attachments'] = attachments;
      // }
      // Send message and receive streaming response
      const token = localStorage.getItem('user-token');
      // const response = await fetchWithAuth('/api/ai/search', {
      const response = await fetchWithAuth('/api/clients/chat', {
        method: 'POST',
        body: JSON.stringify(formData),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        signal: abortController.signal,
      });
      // const response = {
      //   token: token,
      //   ok: false,
      //   status: false,
      //   statusText: 'test',
      //   body: {
      //     getReader: () => ({
      //       read: () => ({ done: true, value: undefined }),
      //     }),
      //   },
      // };
      if (!response.ok) {
        // 🔥 HTTP 状态码错误，尝试读取错误消息
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          console.error('Failed to parse error response:', e);
        }

        // 发送错误消息到前端
        this.handleMessageInContext(
          {
            content: errorMessage,
            error: `HTTP ${response.status}`,
            // include conversationId so frontend can route and clear loading state
            conversationId,
          },
          'message_error',
          pendingId
        );
        return;
      }
      // Handle the streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = 'unstart';
      let ended = false;
      let hasReceivedAnyEvent = false; // 🔥 跟踪是否收到任何事件
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (!ended) {
            ended = true;

            // 🔥 Skill 任务：SSE 流提前结束是正常行为（实际内容通过 WebSocket 传输）
            // 优先检查（在 buffer 解析和错误处理之前），确保覆盖所有情况
            // 使用类级别 Set 检查，最可靠（不受 currentMessages 覆盖或 store 时序影响）
            if (this.skillTaskPendingIds.has(pendingId)) {
              console.log(
                '🎯 Skill task: SSE stream ended early (expected), content comes via WebSocket'
              );

              // 🔥 不发送终结消息（之前的 this.onMessage(finalMsg) 会触发 handleIncomingMessage
              // 导致会话路由/状态更新异常，跳转到欢迎页）
              // WebSocket handler 会完全接管消息的创建和渲染

              // 静默清理 SSE 状态
              this.currentMessages.delete(requestKey);
              this.activeRequests.delete(pendingId);
              // 🔥 移除活跃流式消息（避免 Stop 按钮一直显示）
              useUIStore
                .getState()
                .removeActiveStreamingMessage(conversationId, pendingId);
              break;
            }

            // 流意外中断（未收到 message_end），保留已接收内容并标记错误
            let errorMessage = 'Request was interrupted unexpectedly.';
            let errorCode: number | null = null;

            // 🔥 关键修复：尝试从 buffer 解析业务错误信息（如 code: 50502）
            if (buffer.trim()) {
              try {
                const parsedData = JSON.parse(buffer);
                if (parsedData.code && parsedData.code !== 200) {
                  errorCode = parsedData.code;
                  errorMessage =
                    parsedData.message || parsedData.msg || errorMessage;
                  console.error(
                    '❌ Business error detected in response:',
                    errorCode,
                    errorMessage
                  );
                }
              } catch (error) {
                console.error('Error parsing final buffer as JSON:', error);
                // 解析失败也继续处理，使用默认错误消息
              }
            }

            // 🔥 如果没有收到任何事件但有业务错误，说明后端返回的是纯 JSON 错误
            // 而不是 SSE 流，需要直接显示错误
            if (!hasReceivedAnyEvent && errorCode) {
              console.warn(
                '⚠️ No SSE events received, but business error detected:',
                errorCode
              );
              this.handleMessageInContext(
                {
                  content: errorMessage,
                  error: `Error ${errorCode}`,
                  // no SSE events were received - include conversationId to route error
                  conversationId,
                },
                'message_error',
                pendingId
              );
              break;
            }

            console.warn(
              '⚠️ Stream ended unexpectedly without message_end, preserving received content'
            );

            // 关键修复：发送 message_error 时会保留已接收的内容
            // handleMessageInContext 会查找 currentMessages 并保留 blocks
            this.handleMessageInContext(
              {
                content: errorMessage,
                error: 'Stream ended without receiving message_end event',
                // include conversationId to allow frontend to clear loading state
                conversationId,
              },
              'message_error',
              pendingId
            );
          }
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep the last incomplete line in buffer
        for (const line of lines) {
          if (line.startsWith('event:')) {
            const lineEvent = line.split(':')[1]?.trim();
            eventType = lineEvent || 'unknown';
            hasReceivedAnyEvent = true; // 🔥 标记已收到事件
            continue;
          }
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            if (dataStr.trim()) {
              try {
                const data = JSON.parse(dataStr);

                // 🔥 关键修复：检查业务错误码（如 code: 50502）
                if (data.code && data.code !== 200) {
                  console.error(
                    '❌ Business error in SSE data:',
                    data.code,
                    data.message || data.msg
                  );
                  // 🔥 Skill 任务：业务错误也静默处理，不显示错误消息
                  if (this.skillTaskPendingIds.has(pendingId)) {
                    console.log(
                      '🎯 Skill task: SSE business error suppressed, content comes via WebSocket'
                    );
                    this.currentMessages.delete(requestKey);
                    this.activeRequests.delete(pendingId);
                    useUIStore
                      .getState()
                      .removeActiveStreamingMessage(conversationId, pendingId);
                    ended = true;
                    break;
                  }
                  // 发送错误消息
                  this.handleMessageInContext(
                    {
                      content: data.message || data.msg || 'Unknown error',
                      error: `Error ${data.code}`,
                      // include conversationId so frontend can route and clear loading state
                      conversationId,
                    },
                    'message_error',
                    pendingId
                  );
                  ended = true;
                  break;
                }

                if (eventType === 'message_end') {
                  ended = true;
                }
                this.handleMessageInContext(data, eventType, pendingId);
              } catch (error) {
                console.error('Error parsing SSE data:', error);
                console.error('Raw data received:', JSON.stringify(dataStr));
              }
            }
          }
        }

        // 🔥 如果在解析过程中发现错误并已处理，退出循环
        if (ended && buffer.trim()) {
          try {
            const parsedData = JSON.parse(buffer);
            if (parsedData.code && parsedData.code !== 200) {
              break;
            }
          } catch (e) {
            // 忽略解析错误
          }
        }
      }
    } catch (error) {
      // Check if the error is due to request cancellation
      if (error instanceof Error && error.name === 'AbortError') {
        // Update current message to stop streaming if exists
        const requestKey = `request_${pendingId}`;
        const cancelledMessage = this.currentMessages.get(requestKey);
        if (cancelledMessage) {
          // 修复：保留已接收的内容，添加停止标记
          const cancelledBlocks = [
            ...cancelledMessage.blocks,
            {
              type: 'text' as const,
              content: '\n\n_[Response stopped by user]_',
            },
          ];
          const finalCancelledMessage = this.createMessage({
            id: cancelledMessage.id,
            previousMessageId: cancelledMessage.previousMessageId,
            backendId: cancelledMessage.backendId,
            blocks: cancelledBlocks,
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: false,
            isCanceled: true, // 🔥 修复：明确设置 isCanceled 属性
          });

          // 关键修复：保留 conversationId，确保消息能正确路由
          if (cancelledMessage.conversationId) {
            (finalCancelledMessage as any).conversationId =
              cancelledMessage.conversationId;
          }
          this.onMessage(finalCancelledMessage);

          // 清理已取消的消息状态
          this.currentMessages.delete(requestKey);
        }
        // 🔥 新增：取消请求时清除等待状态，防止发送按钮永久禁用
        useUIStore.getState().setIsWaitingForMessageInit(false);
        return;
      }
      console.error('Error sending message:', error);
      // 发生错误时清除等待状态，防止发送按钮永久禁用
      useUIStore.getState().setIsWaitingForMessageInit(false);

      // 🔥 Skill 任务：网络错误也静默处理，不显示错误消息（实际内容通过 WebSocket 传输）
      if (this.skillTaskPendingIds.has(pendingId)) {
        console.log(
          '🎯 Skill task: network error suppressed, content comes via WebSocket'
        );
        const rk = `request_${pendingId}`;
        this.currentMessages.delete(rk);
        this.activeRequests.delete(pendingId);
        useUIStore
          .getState()
          .removeActiveStreamingMessage(conversationId, pendingId);
        return;
      }

      // Try to update existing message with error, or create new one
      const requestKey = `request_${pendingId}`;
      const errorMessage = this.currentMessages.get(requestKey);
      if (errorMessage) {
        // Update existing message with error
        this.onMessage(
          this.createMessage({
            id: errorMessage.id,
            previousMessageId: errorMessage.previousMessageId,
            backendId: errorMessage.backendId,
            blocks: [
              ...errorMessage.blocks,
              {
                type: 'text',
                content: `\n\n**Error:** ${error}`,
              },
            ],
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: false,
          })
        );
      } else {
        // Create new error message
        this.onMessage(
          this.createMessage({
            id: generateId(),
            blocks: [
              {
                type: 'text',
                content: `Error sending message: ${error}`,
              },
            ],
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: false,
          })
        );
      }

      // 🔥 修复：网络错误时正确清理状态和更新任务队列
      // 1. 更新任务状态为 error
      useUIStore
        .getState()
        .updateTaskStatus(
          pendingId,
          'error',
          undefined,
          undefined,
          error instanceof Error
            ? error.message
            : 'Network error or request failed'
        );

      // 2. 清理 activeStreamingMessage，解除 loading 状态
      useUIStore
        .getState()
        .removeActiveStreamingMessage(conversationId, pendingId);

      // 3. 通知下一个任务可以开始（如果有的话）
      const nextTask = useUIStore.getState().getNextPendingTask();
      if (nextTask) {
        console.log('🔄 Triggering next task after error:', nextTask.id);
        // 错误发生后，下一个任务不应该依赖前一个失败任务的 messageId
        window.dispatchEvent(
          new CustomEvent('sendNextPendingTask', {
            detail: {
              task: nextTask,
              previousMessageId: null, // 不链接到失败的消息
            },
          })
        );
      }

      // 🔥 不再重新抛出错误，避免上层处理不当导致状态卡死
      // throw error; // 移除，防止状态无法恢复
    } finally {
      // Clean up this request's resources
      this.activeRequests.delete(pendingId);
      // 清理基于请求的消息状态
      const requestKey = `request_${pendingId}`;
      this.currentMessages.delete(requestKey);
      // 🔥 清理 skill 任务追踪
      this.skillTaskPendingIds.delete(pendingId);
    }
  }
  /**
   * 停止请求并返回所有活跃的 completionId
   * @param pendingId 可选，指定要停止的请求ID。
   *   - 如果是 conversationId（不包含 ':'），停止该会话的所有请求
   *   - 如果是完整键（conversationId:messageId），停止特定请求
   *   - 如果不传，停止所有请求（向后兼容）
   * @returns 所有被停止请求的 completionId 数组
   */
  stopRequest(pendingId?: string): string[] {
    const completionIds: string[] = [];

    console.log('stopRequest:', pendingId);

    if (pendingId) {
      // 🔥 检查是否是 conversationId（不包含 ':'）还是完整的请求键（包含 ':'）
      if (!pendingId.includes(':')) {
        // 🔥 这是一个 conversationId - 只停止该会话的所有请求，不影响其他会话
        const conversationId = pendingId;
        console.log('🛑 停止 conversation 的所有请求:', conversationId);

        // 找到所有属于这个 conversation 的请求键
        const keysToStop: string[] = [];
        this.activeRequests.forEach((_, key) => {
          if (key.startsWith(conversationId + ':')) {
            keysToStop.push(key);
          }
        });

        console.log('🛑 找到要停止的请求:', keysToStop);

        // 停止这些请求
        keysToStop.forEach(key => {
          const controller = this.activeRequests.get(key);
          if (controller) {
            controller.abort();
            this.activeRequests.delete(key);
          }
        });

        // 从 currentMessages 收集该 conversation 的 completionIds
        this.currentMessages.forEach(msg => {
          if (
            (msg as any).conversationId === conversationId &&
            msg.completionId
          ) {
            if (!completionIds.includes(msg.completionId)) {
              completionIds.push(msg.completionId);
            }
          }
        });
      } else {
        // 这是一个完整的请求键（conversationId:messageId）- 停止特定请求
        const controller = this.activeRequests.get(pendingId);
        if (controller) {
          controller.abort();
          this.activeRequests.delete(pendingId);
        }
        // Get completionId for this specific request
        const requestKey = `request_${pendingId}`;
        const msg = this.currentMessages.get(requestKey);
        if (msg?.completionId) {
          completionIds.push(msg.completionId);
        }
      }
    } else {
      // Stop all active requests - 先收集所有 completionId（向后兼容）
      this.currentMessages.forEach(msg => {
        if (msg.completionId) {
          completionIds.push(msg.completionId);
        }
      });

      // 然后停止所有请求
      this.activeRequests.forEach(controller => {
        controller.abort();
      });
      this.activeRequests.clear();
    }

    console.log('🛑 stopRequest: 收集到的 completionIds:', completionIds);
    return completionIds;
  }
}
